import { createAnalytics, type Storage } from '@simple-life-medical/med-analytics-ts-web'
import platform from 'platform'
import { config } from '@/config'

// Endpoint + key come from env (see docs/architecture/deployment.md). When either is
// absent — local dev without secrets, or a build that didn't inject them — the module
// falls back to a no-op with the same shape, so call-sites never need a guard.
const apiKey: string = import.meta.env.VITE_MED_ANALYTICS_KEY ?? ''
const apiUrl: string = import.meta.env.VITE_MED_ANALYTICS_URL ?? ''

type AnalyticsInstance = ReturnType<typeof createAnalytics>

const localStorageAdapter: Storage = {
	get(key) {
		try {
			return localStorage.getItem(key)
		} catch {
			return null
		}
	},
	set(key, value) {
		try {
			localStorage.setItem(key, value)
		} catch {
			/* ignore quota / disabled storage */
		}
	},
	remove(key) {
		try {
			localStorage.removeItem(key)
		} catch {
			/* ignore */
		}
	},
}

// Same public shape as a real instance, so consumers don't branch on availability.
const noopAnalytics: AnalyticsInstance = {
	setContext: () => {},
	track: () => {},
	flush: () => Promise.resolve(),
}

// --- Non-prod console logger (ported from med-web-funnel's analytics module) ---------
// Mirrors every track()/setContext() to the DevTools console (collapsed groups, one
// color per event category) so devs see *what they sent* without decoding protobuf
// bodies in the Network panel.
//
// Gate: `import.meta.env.MODE !== 'production'` — true for `npm run serve` AND for
// `build:dev` (the deployed dev stand), false only for `build:prod`. NOT
// `import.meta.env.DEV`: Vite keys DEV/PROD off NODE_ENV, which `vite build` forces to
// 'production' even under `--mode development`, so a DEV gate strips the logger from
// the dev-stand bundle. The direct `import.meta.env.MODE` access is statically replaced
// at build time, so the whole logger is dead-code-eliminated from prod builds.
const isNonProdBuild = import.meta.env.MODE !== 'production'

const LOGGER_COLORS: Record<'default' | 'ux' | 'cancellation' | 'paywall', string> = {
	default: '#7c3aed', // purple — generic / context
	ux: '#0ea5e9', // blue — ScreenView / ButtonClick / Block* / Overlay* / HeaderView
	cancellation: '#10b981', // green — Cancellation* / Subscription* / SupportEntryPointDone
	paywall: '#f59e0b', // amber — UpdatePaymentMethod* and other Paywall/Payment events
}

const UX_EVENTS = new Set([
	'ScreenView',
	'ButtonClick',
	'BlockView',
	'BlockClick',
	'OverlayView',
	'OverlayClose',
	'HeaderView',
])

function pickColor(eventName: string): string {
	if (eventName.includes('Paywall') || eventName.includes('Checkout') || eventName.includes('Payment')) {
		return LOGGER_COLORS.paywall
	}
	if (
		eventName.startsWith('Cancellation') ||
		eventName.startsWith('Subscription') ||
		eventName === 'SupportEntryPointDone'
	) {
		return LOGGER_COLORS.cancellation
	}
	if (UX_EVENTS.has(eventName)) return LOGGER_COLORS.ux
	return LOGGER_COLORS.default
}

function logTrack(eventName: string, payload: unknown, header: unknown): void {
	const color = pickColor(eventName)
	/* eslint-disable no-console */
	console.groupCollapsed(
		`%cmed-analytics [debug]: %c${eventName}`,
		'font-weight: 400; color: #888',
		`color: ${color}; font-weight: 600`,
	)
	console.log('payload:', payload)
	if (header !== undefined) console.log('header:', header)
	console.groupEnd()
	/* eslint-enable no-console */
}

function logSetContext(partial: unknown): void {
	/* eslint-disable no-console */
	console.groupCollapsed(
		`%cmed-analytics [debug]: %csetContext`,
		'font-weight: 400; color: #888',
		`color: ${LOGGER_COLORS.default}; font-style: italic`,
	)
	console.log(partial)
	console.groupEnd()
	/* eslint-enable no-console */
}

// The methods are intentionally type-erased to `(...args) => void` so we can wrap them
// without `as` casts on every call; origTrack/origSetContext stay bound to the instance.
type ErasedFn = (...args: unknown[]) => void

function attachDebugLogger(instance: AnalyticsInstance): void {
	const origTrack: ErasedFn = instance.track.bind(instance) as ErasedFn
	const origSetContext: ErasedFn = instance.setContext.bind(instance) as ErasedFn

	const wrappedTrack: ErasedFn = (...args) => {
		logTrack(String(args[0]), args[1], args[2])
		origTrack(...args)
	}
	const wrappedSetContext: ErasedFn = (...args) => {
		logSetContext(args[0])
		origSetContext(...args)
	}

	Object.assign(instance, { track: wrappedTrack, setContext: wrappedSetContext })
}

function createInstance(): AnalyticsInstance {
	if (!apiKey || !apiUrl) return noopAnalytics

	const instance = createAnalytics({ apiKey, apiUrl, storage: localStorageAdapter })
	if (isNonProdBuild) attachDebugLogger(instance)

	// Store-independent context only; geo/user/cancellation are merged later from main.ts
	// (those need an active Pinia instance, which isn't ready at module load).
	instance.setContext({
		application: {
			app_id: 'WebFunnel',
			app_platform: 'Web',
			app_version: config.appVersion,
		},
		browser: {
			browser_name: platform.name,
			browser_version: platform.version,
		},
		os: {
			os_name: platform.os?.family,
			os_version: platform.os?.version,
		},
	})
	// No SessionStart here: the event was removed from the schema in SDK 0.3.x
	// (kova_event_schema); session semantics now come from the per-flow events
	// (CancellationSessionStart etc.), and an unknown event name throws at runtime.

	return instance
}

const analytics = createInstance()
export default analytics

// Idempotent: the Auth0 `sub` only changes on logout (which navigates away), so a guard
// against re-sending the same id is enough.
let lastUserId: string | null = null
export function setAnalyticsUser(userId: string | null | undefined): void {
	if (!userId || userId === lastUserId) return
	analytics.setContext({ user: { user_id: userId } })
	lastUserId = userId
}

export function flushAnalytics(): Promise<void> {
	return analytics.flush()
}

// Cap on the flush before a tap that leaves the page. The SDK persists the batch to localStorage
// synchronously before any network I/O, so a slow/unreachable telemetry endpoint only costs
// immediate delivery — it must not dead-button the tap (the SDK's retry back-off can hold flush()
// open for minutes).
const LEAVE_FLUSH_TIMEOUT_MS = 2000

/**
 * Send what is queued before the page goes away — the Auth0 logout redirect, a deeplink that may
 * close the webview. The SDK sends a batch a second after the last event and never on unload, so
 * an event tracked just before leaving is otherwise lost. Waits at most 2 s.
 */
export function flushBeforeLeaving(): Promise<void> {
	return Promise.race([
		analytics.flush(),
		new Promise<void>((resolve) => {
			setTimeout(resolve, LEAVE_FLUSH_TIMEOUT_MS)
		}),
	])
}
